import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import CreditsAppliedPanel from './CreditsAppliedPanel.jsx';
import DpeActionsPanel from './DpeActionsPanel.jsx';
import ExportPreviewPanel from './ExportPreviewPanel.jsx';
import FileHealthPanel from './FileHealthPanel.jsx';
import HistoryPanel from './HistoryPanel.jsx';
import LifecycleAlertsPanel from './LifecycleAlertsPanel.jsx';
import PricingCatalogPanel from './PricingCatalogPanel.jsx';
import RecurringRulesPanel from './RecurringRulesPanel.jsx';
import VarianceActionTrackerPanel from './VarianceActionTrackerPanel.jsx';
import VarianceClassificationRefreshDialog from './VarianceClassificationRefreshDialog.jsx';
import VarianceSummaryPanel from './VarianceSummaryPanel.jsx';
import { adjustedDpeTotalForStatus } from './dpeActionPreviewTotals.js';
import {
  ALL,
  OPERATIONAL_DEFAULT_SORT,
  applyVarianceSummaryView,
  varianceDriverLabel,
  varianceSummaryOptions,
} from './varianceSummaryTableModel.js';
import { isEditableTarget } from './shortcutUtils.js';
import {
  COMPLETED_VIEW,
  ALL_ROWS_VIEW,
  SAVED_VIEWS,
  applySavedView,
  buildFileHealthByBac,
} from './varianceSummaryQueue.js';
import {
  useBillingReconciliationActionSummary,
  useBillingReconciliationCatalog,
  useBillingReconciliationContextWarmStatus,
  useBillingReconciliationCredits,
  useBillingReconciliationDpeActions,
  useBillingReconciliationFileHealthSummary,
  useBillingReconciliationLifecycleAlerts,
  useQueueBillingReconciliationContextWarm,
  useRefreshBillingReconciliationVarianceClassifications,
  useResetBillingReconciliationVarianceReviews,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const TAB_LIMIT = 500;
const FILE_HEALTH_LIMIT = 250;
const DPE_ACTION_PREVIEW_LIMIT = 1000;
const EMPTY_ROWS = [];

const searchableText = (row = {}) => [
  row.bac,
  row.dealerName,
  row.dealerDisplayName,
  row.dpeEnrollmentStatusCd,
  row.brand,
  row.status,
  row.autoDriver,
  row.reviewDriver,
  varianceDriverLabel(row),
  row.accountMatchSummary?.displayName,
  ...(Array.isArray(row.accountMatchSummary?.accounts)
    ? row.accountMatchSummary.accounts.flatMap((account) => [
      account.salesforceAccountName,
      account.salesforceAccountId,
    ])
    : []),
].filter(Boolean).join(' ').toLowerCase();
const EMPTY_OBJECT = {};

const countBy = (rows, predicate) => rows.filter(predicate).length;
const bacKey = (value) => String(value || '').trim();
const listRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};
const PREFETCH_WINDOW_SIZE = 5;
const prefetchTargetForRow = (row = {}) => {
  const bac = bacKey(row.bac);
  if (!bac) return null;
  return {
    bac,
    varianceId: row.id || row.varianceId || null,
  };
};
const nextBacsForPrefetch = (rows = [], currentIndex = 0, size = PREFETCH_WINDOW_SIZE) => {
  if (!rows.length || rows.length === 1) return [];
  const result = [];
  for (let index = currentIndex + 1; index < rows.length && result.length < size; index += 1) {
    const target = prefetchTargetForRow(rows[index]);
    if (target) result.push(target);
  }
  return result;
};

export const applyDpeActionPreviewToRows = (rows = [], dpeActions = []) => {
  if (!rows.length || !dpeActions.length) return rows;
  const actionsByBac = dpeActions.reduce((map, action) => {
    const key = bacKey(action.bac || action.vmtPayload?.bac || action.sourceEvidence?.bac);
    if (!key) return map;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(action);
    return map;
  }, new Map());
  if (!actionsByBac.size) return rows;
  return rows.map((row) => {
    const actions = actionsByBac.get(bacKey(row.bac));
    if (!actions?.length) return row;
    const dpeTotal = adjustedDpeTotalForStatus({
      baseTotal: row.dpeTotal,
      dpeActions: actions,
      activeStatus: 'LIVE',
    });
    if (Math.round(Number(row.dpeTotal) || 0) === Math.round(Number(dpeTotal) || 0)) return row;
    return {
      ...row,
      dpeTotal,
      originalTotals: {
        ...(row.originalTotals || {}),
        dpeTotal: row.originalTotals?.dpeTotal ?? row.dpeTotal,
      },
      totalStatuses: {
        ...(row.totalStatuses || {}),
        dpe: row.totalStatuses?.dpe || 'dpe_action_preview',
      },
    };
  });
};

const WARM_ACTIVE_STATUSES = new Set(['pending', 'queued', 'processing', 'invalidated', 'warming']);

export const applyContextWarmStatusToRows = (rows = [], status = null) => {
  if (!rows.length || !status) return rows;
  const rowsByBac = new Map((status.bacs || [])
    .map((row) => [bacKey(row.bac), row])
    .filter(([bac]) => bac));
  const aggregateProcessed = !rowsByBac.size
    && Number(status.total || 0) > 0
    && Number(status.processed ?? status.warmed ?? 0) >= Number(status.total || 0)
    && Number(status.queued || 0) === 0
    && Number(status.processing || 0) === 0
    && Number(status.pending || 0) === 0;
  if (!rowsByBac.size && !aggregateProcessed) return rows;
  let changed = false;
  const nextRows = rows.map((row) => {
    const warmRow = rowsByBac.get(bacKey(row.bac));
    if (warmRow) {
      const patch = {
        contextWarmStatus: warmRow.contextWarmStatus || (warmRow.contextWarmedAt || warmRow.warmedAt ? 'processed' : 'pending'),
        contextWarmedAt: warmRow.contextWarmedAt || warmRow.warmedAt || null,
        contextWarmError: warmRow.contextWarmError || null,
        contextWarmStartedAt: warmRow.contextWarmStartedAt || null,
        contextWarmLastAttemptAt: warmRow.contextWarmLastAttemptAt || null,
        contextWarmUpdatedAt: warmRow.contextWarmUpdatedAt || null,
        contextWarmStale: Boolean(warmRow.contextWarmStale),
      };
      if (
        row.contextWarmStatus === patch.contextWarmStatus
        && row.contextWarmedAt === patch.contextWarmedAt
        && row.contextWarmError === patch.contextWarmError
        && row.contextWarmStartedAt === patch.contextWarmStartedAt
        && row.contextWarmLastAttemptAt === patch.contextWarmLastAttemptAt
        && row.contextWarmUpdatedAt === patch.contextWarmUpdatedAt
        && Boolean(row.contextWarmStale) === patch.contextWarmStale
      ) return row;
      changed = true;
      return { ...row, ...patch };
    }
    if (!aggregateProcessed || !WARM_ACTIVE_STATUSES.has(String(row.contextWarmStatus || '').trim().toLowerCase())) return row;
    changed = true;
    return {
      ...row,
      contextWarmStatus: 'processed',
      contextWarmedAt: status.latestWarmedAt || row.contextWarmedAt || row.warmedAt || null,
      contextWarmError: null,
      contextWarmStale: false,
    };
  });
  return changed ? nextRows : rows;
};

export const tabBadgeCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const TabLabel = ({ icon, title, badge = null, badgeTooltip = '' }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 32 }}>
    {icon}
    <Typography component="span" variant="body2" sx={{ fontWeight: 800 }}>
      {title}
    </Typography>
    {badge !== null && (
      <Tooltip title={badgeTooltip} disableHoverListener={!badgeTooltip}>
        <Chip
          size="small"
          color={tabBadgeCount(badge) > 0 ? 'warning' : 'default'}
          variant="outlined"
          label={tabBadgeCount(badge).toLocaleString()}
          sx={{ height: 22, minWidth: 26, fontWeight: 800 }}
        />
      </Tooltip>
    )}
  </Stack>
);

const TabPanel = ({ active, children }) => (
  <Box role="tabpanel" hidden={!active} sx={{ pt: 2 }}>
    {active ? children : null}
  </Box>
);

const RETRYABLE_EXPIRED_WARM_STATUSES = new Set(['invalidated', 'failed', 'unavailable']);
const ACTIVE_WARM_STATUSES = new Set(['queued', 'processing']);

const expiredWarmBacs = (status = {}) => (status?.bacs || [])
  .filter((row) => {
    const warmStatus = String(row?.contextWarmStatus || '').trim().toLowerCase();
    return RETRYABLE_EXPIRED_WARM_STATUSES.has(warmStatus)
      || (row?.contextWarmStale === true && ACTIVE_WARM_STATUSES.has(warmStatus));
  })
  .map((row) => row.bac)
  .filter(Boolean);

const ContextWarmStatus = ({ status, isLoading, isQueueing, onQueue, onQueueExpired }) => {
  if (!status && !isLoading) return null;
  const warmed = Number(status?.warmed || 0);
  const total = Number(status?.total || 0);
  const pending = Math.max(0, Number(status?.pending || 0));
  const queued = Math.max(0, Number(status?.queued || 0));
  const processing = Math.max(0, Number(status?.processing || 0));
  const stale = Math.max(0, Number(status?.stale || 0));
  const invalidated = Math.max(0, Number(status?.invalidated || 0));
  const failed = Math.max(0, Number(status?.failed || 0) + Number(status?.unavailable || 0));
  const active = Math.max(0, queued + processing + pending);
  const expiredBacs = expiredWarmBacs(status);
  const hasBacDetail = Array.isArray(status?.bacs);
  const expiredCount = hasBacDetail ? expiredBacs.length : 0;
  const progress = total > 0 ? Math.round((warmed / total) * 100) : 0;
  const ready = Boolean(status?.ready);
  const last = status?.latestWarmedAt ? new Date(status.latestWarmedAt).toLocaleString() : null;
  const detail = [
    processing ? `${processing.toLocaleString()} processing` : null,
    queued ? `${queued.toLocaleString()} queued` : null,
    invalidated ? `${invalidated.toLocaleString()} expired` : null,
    stale ? `${stale.toLocaleString()} stale` : null,
    failed ? `${failed.toLocaleString()} failed` : null,
  ].filter(Boolean).join(' · ');
  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 1,
        px: 0.5,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              color={ready ? 'success' : failed ? 'error' : active > 0 ? 'warning' : 'default'}
              variant="outlined"
              label={ready ? 'BAC contexts warmed' : `BAC contexts warmed: ${warmed.toLocaleString()} / ${total.toLocaleString()}`}
              sx={{ fontWeight: 800 }}
            />
            <Typography variant="caption" color="text.secondary">
              {detail || (last ? `Snapshot cache from ${last}` : 'Snapshot cache warms after upload and stays stable until refreshed.')}
            </Typography>
          </Stack>
          {active > 0 && <LinearProgress variant={total > 0 ? 'determinate' : 'indeterminate'} value={progress} sx={{ maxWidth: 420 }} />}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onQueueExpired?.(expiredBacs)}
            disabled={!status?.runId || !hasBacDetail || expiredBacs.length <= 0}
            startIcon={<CloudSyncOutlinedIcon fontSize="small" />}
          >
            {isQueueing ? 'Queueing...' : `Warm expired${expiredCount ? ` (${expiredCount.toLocaleString()})` : ''}`}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onQueue}
            disabled={!status?.runId}
            startIcon={<CloudSyncOutlinedIcon fontSize="small" />}
            title="Force refreshes every BAC context for this round."
          >
            {isQueueing ? 'Queueing...' : 'Warm all contexts'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

const BillingReconciliationWorkspace = ({
  drawerOpen,
  filtered,
  onExport,
  onOpenBac,
  onRowClick,
  onSearchChange,
  program,
  reconciled,
  runId,
  search,
  selectedBac,
  serviceMonth,
  iterations = [],
  summary,
  threshold,
  varianceError = null,
  varianceLoading = false,
  varianceListMeta = null,
  onRetryVariances = null,
  onDrawerNavigationChange = null,
  onBackfillImported = null,
  onReviewsReset = null,
}) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState('variances');
  const [tableStatus, setTableStatus] = useState(ALL);
  const [tableDriver, setTableDriver] = useState(ALL);
  const [tableEnrollment, setTableEnrollment] = useState(ALL);
  const [tableGroupBy, setTableGroupBy] = useState('none');
  const [tableSavedView, setTableSavedView] = useState(ALL);
  const [tableSortBy, setTableSortBy] = useState(OPERATIONAL_DEFAULT_SORT);
  const [tableSortDirection, setTableSortDirection] = useState('desc');
  const [classificationRefreshOpen, setClassificationRefreshOpen] = useState(false);
  const [classificationRefreshResult, setClassificationRefreshResult] = useState(null);
  const [includeReviewedClassifications, setIncludeReviewedClassifications] = useState(false);
  const [selectedVarianceIds, setSelectedVarianceIds] = useState(() => new Set());
  const [resetReviewsConfirmOpen, setResetReviewsConfirmOpen] = useState(false);
  const lifecycleQuery = useBillingReconciliationLifecycleAlerts(
    { runId, dismissed: false, limit: TAB_LIMIT },
    { enabled: Boolean(runId) && activeTab === 'lifecycle' },
  );
  const fileHealthSummaryQuery = useBillingReconciliationFileHealthSummary(
    { runId, summaryMode: 'counts' },
    { enabled: Boolean(runId) },
  );
  const creditsQuery = useBillingReconciliationCredits(
    { runId, limit: TAB_LIMIT },
    { enabled: Boolean(runId) && activeTab === 'credits' },
  );
  const actionSummaryQuery = useBillingReconciliationActionSummary(
    { runId },
    { enabled: Boolean(runId) && ['variances', 'tracker', 'exports'].includes(activeTab) },
  );
  const dpeActionsQuery = useBillingReconciliationDpeActions(
    { runId, limit: DPE_ACTION_PREVIEW_LIMIT },
    { enabled: Boolean(runId) && activeTab === 'dpeActions' },
  );
  const contextWarmStatusQuery = useBillingReconciliationContextWarmStatus(
    { runId, contextMode: 'critical' },
    { enabled: Boolean(runId) },
  );
  const queueContextWarm = useQueueBillingReconciliationContextWarm();
  const refreshClassifications = useRefreshBillingReconciliationVarianceClassifications();
  const resetVarianceReviews = useResetBillingReconciliationVarianceReviews();
  const catalogQuery = useBillingReconciliationCatalog(
    { program, limit: TAB_LIMIT },
    { enabled: Boolean(program) },
  );

  const lifecycleAlerts = lifecycleQuery.data || EMPTY_ROWS;
  const serverFileHealthSummary = fileHealthSummaryQuery.data || EMPTY_OBJECT;
  const openFileHealthTotal = Number(serverFileHealthSummary.open ?? 0);
  const credits = creditsQuery.data || EMPTY_ROWS;
  const dpeActions = listRows(dpeActionsQuery.data);
  const reconciledWithWarmStatus = useMemo(
    () => applyContextWarmStatusToRows(reconciled, contextWarmStatusQuery.data),
    [contextWarmStatusQuery.data, reconciled],
  );
  const reconciledWithDpePreview = useMemo(
    () => applyDpeActionPreviewToRows(reconciledWithWarmStatus, dpeActions),
    [dpeActions, reconciledWithWarmStatus],
  );
  const filteredWithDpePreview = useMemo(
    () => applyDpeActionPreviewToRows(applyContextWarmStatusToRows(filtered, contextWarmStatusQuery.data), dpeActions),
    [contextWarmStatusQuery.data, dpeActions, filtered],
  );
  const exportSummaryLoaded = Boolean(actionSummaryQuery.data);
  const exportSummary = actionSummaryQuery.data || { total: 0, draftCount: 0, byLane: {} };
  const recurringSuggestionCount = Number(summary.recurringSuggestions?.total || 0);
  const catalog = catalogQuery.data || EMPTY_ROWS;
  const fileErrors = Number(serverFileHealthSummary.byTier?.error?.open ?? 0);
  const handleTableSortChange = (nextSortBy) => {
    if (tableSortBy === nextSortBy) {
      setTableSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setTableSortBy(nextSortBy);
    setTableSortDirection(['gmTotal', 'cpqTotal', 'dpeTotal', 'appSheetTotal'].includes(nextSortBy) ? 'desc' : 'asc');
  };
  const resetTableView = () => {
    setTableStatus(ALL);
    setTableDriver(ALL);
    setTableEnrollment(ALL);
    setTableGroupBy('none');
    setTableSavedView(ALL);
    setTableSortBy(OPERATIONAL_DEFAULT_SORT);
    setTableSortDirection('desc');
    onSearchChange?.('');
  };
  const refreshDashboardData = useCallback(async () => {
    await Promise.allSettled([
      onRetryVariances?.(),
      contextWarmStatusQuery.refetch?.(),
      lifecycleQuery.refetch?.(),
      fileHealthSummaryQuery.refetch?.(),
      creditsQuery.refetch?.(),
      actionSummaryQuery.refetch?.(),
      dpeActionsQuery.refetch?.(),
      catalogQuery.refetch?.(),
    ]);
  }, [actionSummaryQuery, catalogQuery, contextWarmStatusQuery, creditsQuery, dpeActionsQuery, fileHealthSummaryQuery, lifecycleQuery, onRetryVariances]);
  const previewClassificationRefresh = async () => {
    setClassificationRefreshResult(null);
    const result = await refreshClassifications.mutateAsync({
      runId,
      dryRun: true,
      includeReviewed: includeReviewedClassifications,
    });
    setClassificationRefreshResult(result);
  };
  const commitClassificationRefresh = async () => {
    setClassificationRefreshResult(null);
    const result = await refreshClassifications.mutateAsync({
      runId,
      dryRun: false,
      includeReviewed: includeReviewedClassifications,
    });
    setClassificationRefreshResult(result);
    await refreshDashboardData();
  };
  const fileHealthByBac = useMemo(
    () => buildFileHealthByBac({ serverFileHealthSummary }),
    [serverFileHealthSummary],
  );
  const tableOptions = useMemo(() => ({
    ...varianceSummaryOptions(reconciledWithDpePreview),
    savedViews: SAVED_VIEWS.map((view) => ({
      ...view,
      count: applySavedView(reconciledWithDpePreview, view.value, fileHealthByBac).length,
    })),
  }), [fileHealthByBac, reconciledWithDpePreview]);
  const searchedRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return reconciledWithDpePreview;
    return reconciledWithDpePreview.filter((row) => searchableText(row).includes(query));
  }, [reconciledWithDpePreview, search]);
  const tableFilteredRows = useMemo(() => {
    const sourceRows = [COMPLETED_VIEW, ALL_ROWS_VIEW].includes(tableSavedView) ? searchedRows : filteredWithDpePreview;
    const filteredByControls = applyVarianceSummaryView(sourceRows, {
      status: tableStatus,
      driver: tableDriver,
      enrollment: tableEnrollment,
      sortBy: tableSortBy,
      sortDirection: tableSortDirection,
    });
    return applySavedView(filteredByControls, tableSavedView, fileHealthByBac);
  }, [fileHealthByBac, filteredWithDpePreview, searchedRows, tableDriver, tableEnrollment, tableSavedView, tableSortBy, tableSortDirection, tableStatus]);
  const visibleVarianceIds = useMemo(() => (
    tableFilteredRows.map((row) => row.id || row.varianceId).filter(Boolean).map(String)
  ), [tableFilteredRows]);
  const selectedVisibleVarianceIds = useMemo(() => (
    visibleVarianceIds.filter((id) => selectedVarianceIds.has(id))
  ), [selectedVarianceIds, visibleVarianceIds]);
  const allVisibleSelected = visibleVarianceIds.length > 0 && selectedVisibleVarianceIds.length === visibleVarianceIds.length;
  const someVisibleSelected = selectedVisibleVarianceIds.length > 0 && !allVisibleSelected;
  useEffect(() => {
    setSelectedVarianceIds((current) => {
      if (!current.size) return current;
      const allowed = new Set(visibleVarianceIds);
      const next = new Set([...current].filter((id) => allowed.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [visibleVarianceIds]);
  const selectedIndex = tableFilteredRows.findIndex((row) => String(row.bac) === String(selectedBac));
  const openQueueRow = useCallback((direction = 1) => {
    if (!tableFilteredRows.length) return;
    const base = selectedIndex >= 0 ? selectedIndex : direction > 0 ? -1 : 0;
    const nextIndex = (base + direction + tableFilteredRows.length) % tableFilteredRows.length;
    onRowClick(tableFilteredRows[nextIndex].bac);
  }, [onRowClick, selectedIndex, tableFilteredRows]);
  const drawerNavigation = useMemo(() => {
    const total = tableFilteredRows.length;
    if (!total) {
      return {
        currentBac: selectedBac || null,
        currentIndex: -1,
        position: 0,
        total: 0,
        previousBac: null,
        nextBac: null,
        onPrevious: null,
        onNext: null,
      };
    }
    const currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
    const previousIndex = (currentIndex - 1 + total) % total;
    const nextIndex = (currentIndex + 1) % total;
    return {
      currentBac: tableFilteredRows[currentIndex]?.bac || selectedBac || null,
      currentIndex,
      position: currentIndex + 1,
      total,
      previousBac: tableFilteredRows[previousIndex]?.bac || null,
      nextBac: tableFilteredRows[nextIndex]?.bac || null,
      prefetchBacs: nextBacsForPrefetch(tableFilteredRows, currentIndex),
      onPrevious: total > 1 && tableFilteredRows[previousIndex]?.bac
        ? () => onRowClick(tableFilteredRows[previousIndex].bac)
        : null,
      onNext: total > 1 && tableFilteredRows[nextIndex]?.bac
        ? () => onRowClick(tableFilteredRows[nextIndex].bac)
        : null,
    };
  }, [onRowClick, selectedBac, selectedIndex, tableFilteredRows]);
  const unreviewedCatalog = useMemo(() => countBy(catalog, (row) => !row.reviewed), [catalog]);
  useEffect(() => {
    onDrawerNavigationChange?.(drawerNavigation);
  }, [drawerNavigation, onDrawerNavigationChange]);
  useEffect(() => {
    setActiveTab('variances');
    setTableStatus(ALL);
    setTableDriver(ALL);
    setTableEnrollment(ALL);
    setTableGroupBy('none');
    setTableSavedView(ALL);
    setTableSortBy(OPERATIONAL_DEFAULT_SORT);
    setTableSortDirection('desc');
    setClassificationRefreshResult(null);
    setSelectedVarianceIds(new Set());
  }, [runId]);

  const toggleVarianceSelection = useCallback((row, checked) => {
    const id = row?.id || row?.varianceId;
    if (!id) return;
    const key = String(id);
    setSelectedVarianceIds((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const toggleAllVisibleVariances = useCallback((checked) => {
    setSelectedVarianceIds((current) => {
      const next = new Set(current);
      if (checked) visibleVarianceIds.forEach((id) => next.add(String(id)));
      else visibleVarianceIds.forEach((id) => next.delete(String(id)));
      return next;
    });
  }, [visibleVarianceIds]);

  const resetSelectedVarianceReviews = useCallback(async () => {
    if (!runId || resetVarianceReviews.isPending) return;
    if (!selectedVisibleVarianceIds.length) {
      setResetReviewsConfirmOpen(true);
      return;
    }
    const result = await resetVarianceReviews.mutateAsync({ runId, varianceIds: selectedVisibleVarianceIds });
    onReviewsReset?.({ bacs: result?.bacs || [], all: false });
    setSelectedVarianceIds(new Set());
    await refreshDashboardData();
  }, [onReviewsReset, refreshDashboardData, resetVarianceReviews, runId, selectedVisibleVarianceIds]);

  const resetAllVarianceReviews = useCallback(async () => {
    if (!runId || resetVarianceReviews.isPending) return;
    await resetVarianceReviews.mutateAsync({ runId, all: true });
    onReviewsReset?.({ all: true });
    setResetReviewsConfirmOpen(false);
    setSelectedVarianceIds(new Set());
    await refreshDashboardData();
  }, [onReviewsReset, refreshDashboardData, resetVarianceReviews, runId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (drawerOpen || isEditableTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === 'n') {
        event.preventDefault();
        openQueueRow(1);
      }
      if (key === 'p') {
        event.preventDefault();
        openQueueRow(-1);
      }
      if (key === 'e') {
        event.preventDefault();
        setActiveTab('exports');
      }
      if (key === 't') {
        event.preventDefault();
        setActiveTab('tracker');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, openQueueRow]);

  const tabs = [
    {
      id: 'variances',
      title: 'Variance Summary',
      icon: <TableChartOutlinedIcon fontSize="small" />,
      badge: summary.toReview,
      badgeTooltip: 'Open variances',
    },
    {
      id: 'tracker',
      title: 'Action Tracker',
      icon: <PlaylistAddCheckOutlinedIcon fontSize="small" />,
      badge: exportSummaryLoaded ? Number(exportSummary.total || 0) + Number(exportSummary.draftCount || 0) : 0,
      badgeTooltip: exportSummaryLoaded ? 'Saved and draft actions' : 'Loads when available',
    },
    {
      id: 'lifecycle',
      title: 'Lifecycle',
      icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
      badge: lifecycleAlerts.length,
      badgeTooltip: 'Active lifecycle alerts',
    },
    {
      id: 'fileHealth',
      title: 'File Health',
      icon: <FactCheckOutlinedIcon fontSize="small" />,
      badge: openFileHealthTotal,
      badgeTooltip: fileErrors ? `${fileErrors.toLocaleString()} variance-linked file health findings` : 'File health findings',
    },
    {
      id: 'credits',
      title: 'Credits',
      icon: <ReceiptLongOutlinedIcon fontSize="small" />,
      badge: credits.length,
      badgeTooltip: 'Credit rows',
    },
    {
      id: 'dpeActions',
      title: 'DPE Actions',
      icon: <CloudSyncOutlinedIcon fontSize="small" />,
      badge: dpeActions.filter((action) => action.queueStatus !== 'sent').length,
      badgeTooltip: 'Queued DPE/VMT actions',
    },
    {
      id: 'history',
      title: 'History',
      icon: <HistoryEduOutlinedIcon fontSize="small" />,
      badge: null,
    },
    {
      id: 'exports',
      title: 'Exports',
      icon: <FileDownloadOutlinedIcon fontSize="small" />,
      badge: null,
    },
    {
      id: 'pricing',
      title: 'Pricing',
      icon: <SellOutlinedIcon fontSize="small" />,
      badge: catalogQuery.data ? unreviewedCatalog : null,
      badgeTooltip: catalogQuery.data ? 'Unreviewed pricing products' : 'Pricing catalog loading',
    },
    {
      id: 'recurring',
      title: 'Recurring Rules',
      icon: <AutoAwesomeOutlinedIcon fontSize="small" />,
      badge: recurringSuggestionCount,
      badgeTooltip: 'Suggested recurring rules',
    },
  ];

  return (
    <Box>
      <ContextWarmStatus
        status={contextWarmStatusQuery.data}
        isLoading={contextWarmStatusQuery.isLoading}
        isQueueing={queueContextWarm.isPending}
        onQueue={() => queueContextWarm.mutate({ runId, contextMode: 'critical', force: true })}
        onQueueExpired={(bacs) => queueContextWarm.mutate({ runId, contextMode: 'critical', force: false, recoverStaleActive: true, bacs })}
      />
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_event, next) => setActiveTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              alignItems: 'center',
              px: 1.5,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={<TabLabel icon={tab.icon} title={tab.title} badge={tab.badge} badgeTooltip={tab.badgeTooltip} />}
            />
          ))}
        </Tabs>
      </Box>

      <TabPanel active={activeTab === 'variances'}>
        <VarianceSummaryPanel
          drawerOpen={drawerOpen}
          exportDisabled={tableFilteredRows.length === 0}
          filtered={tableFilteredRows}
          isCompact={isCompact}
          onExport={onExport}
          onRowClick={onRowClick}
          onSearchChange={onSearchChange}
          reconciled={reconciledWithDpePreview}
          search={search}
          selectedBac={selectedBac}
          serviceMonth={serviceMonth}
          summary={summary}
          modifiedTotals={{
            gm: actionSummaryQuery.data?.cycleClose?.modifiedGmTotal,
            cpq: actionSummaryQuery.data?.cycleClose?.projectedCpqBillableTotal,
            gmInvoice: actionSummaryQuery.data?.cycleClose?.modifiedGmInvoiceTotal,
            cpqInvoice: actionSummaryQuery.data?.cycleClose?.projectedCpqInvoiceBillableTotal,
            gmInvoiceMissingCount: actionSummaryQuery.data?.cycleClose?.modifiedGmInvoiceMissingCount,
            cpqInvoiceMissingCount: actionSummaryQuery.data?.cycleClose?.projectedCpqInvoiceMissingCount,
            originalGm: actionSummaryQuery.data?.cycleClose?.originalGmBillableTotal,
            originalCpq: actionSummaryQuery.data?.cycleClose?.originalCpqBillableTotal,
            originalCpqInvoice: actionSummaryQuery.data?.cycleClose?.originalCpqInvoiceBillableTotal,
            delta: actionSummaryQuery.data?.cycleClose?.delta,
            unassignedCpq: actionSummaryQuery.data?.cycleClose?.unassignedCpqBillableTotal,
            unassignedCpqCount: actionSummaryQuery.data?.cycleClose?.unassignedCpqBillableCount,
            gmError: actionSummaryQuery.data?.cycleClose?.gmTotalError,
            loaded: Boolean(actionSummaryQuery.data?.cycleClose),
          }}
          contextWarmStatus={contextWarmStatusQuery.data}
          tableView={{
            status: tableStatus,
            driver: tableDriver,
            enrollment: tableEnrollment,
            groupBy: tableGroupBy,
            savedView: tableSavedView,
            sortBy: tableSortBy,
            sortDirection: tableSortDirection,
            options: tableOptions,
            onSavedViewChange: setTableSavedView,
            onStatusChange: setTableStatus,
            onDriverChange: setTableDriver,
            onEnrollmentChange: setTableEnrollment,
            onGroupByChange: setTableGroupBy,
            onSortChange: handleTableSortChange,
            onReset: resetTableView,
          }}
          onRefreshAll={refreshDashboardData}
          onRefreshReasons={() => {
            setClassificationRefreshResult(null);
            refreshClassifications.reset?.();
            setClassificationRefreshOpen(true);
          }}
          threshold={threshold}
          varianceError={varianceError}
          varianceLoading={varianceLoading}
          varianceListMeta={varianceListMeta}
          onRetryVariances={onRetryVariances}
          fileHealthByBac={fileHealthByBac}
          selectedVarianceIds={selectedVarianceIds}
          allVisibleSelected={allVisibleSelected}
          someVisibleSelected={someVisibleSelected}
          onToggleVarianceSelection={toggleVarianceSelection}
          onToggleAllVisibleVariances={toggleAllVisibleVariances}
          onResetVarianceReviews={resetSelectedVarianceReviews}
          resetVarianceReviewsDisabled={!runId}
          resetVarianceReviewsBusy={resetVarianceReviews.isPending}
          selectedVarianceCount={selectedVisibleVarianceIds.length}
        />
        <Dialog
          open={resetReviewsConfirmOpen}
          onClose={() => setResetReviewsConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reset all reviewed variances?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              No variance rows are selected. This will set every reviewed variance in this round back to not reviewed.
              Existing actions, notes, file changes, and evidence links will stay in place.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetReviewsConfirmOpen(false)} disabled={resetVarianceReviews.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={resetAllVarianceReviews}
              disabled={resetVarianceReviews.isPending}
            >
              {resetVarianceReviews.isPending ? 'Resetting...' : 'Reset all reviewed'}
            </Button>
          </DialogActions>
        </Dialog>
        <VarianceClassificationRefreshDialog
          open={classificationRefreshOpen}
          includeReviewed={includeReviewedClassifications}
          onIncludeReviewedChange={(checked) => {
            setIncludeReviewedClassifications(checked);
            setClassificationRefreshResult(null);
          }}
          onClose={() => setClassificationRefreshOpen(false)}
          onPreview={previewClassificationRefresh}
          onCommit={commitClassificationRefresh}
          result={classificationRefreshResult}
          isRunning={refreshClassifications.isPending}
          error={refreshClassifications.error}
        />
      </TabPanel>

      <TabPanel active={activeTab === 'lifecycle'}>
        <LifecycleAlertsPanel runId={runId} limit={TAB_LIMIT} onOpenBac={onOpenBac} />
      </TabPanel>

      <TabPanel active={activeTab === 'tracker'}>
        <VarianceActionTrackerPanel runId={runId} iterations={iterations} onOpenBac={onOpenBac} />
      </TabPanel>

      <TabPanel active={activeTab === 'fileHealth'}>
        <FileHealthPanel runId={runId} limit={FILE_HEALTH_LIMIT} onOpenBac={onOpenBac} />
      </TabPanel>

      <TabPanel active={activeTab === 'credits'}>
        <CreditsAppliedPanel runId={runId} program={program} serviceMonth={serviceMonth} limit={TAB_LIMIT} onOpenBac={onOpenBac} />
      </TabPanel>

      <TabPanel active={activeTab === 'dpeActions'}>
        <DpeActionsPanel
          runId={runId}
          program={program}
          serviceMonth={serviceMonth}
          iterations={iterations}
          limit={TAB_LIMIT}
        />
      </TabPanel>

      <TabPanel active={activeTab === 'history'}>
        <HistoryPanel program={program} serviceMonth={serviceMonth} onBackfillImported={onBackfillImported} />
      </TabPanel>

      <TabPanel active={activeTab === 'exports'}>
        <ExportPreviewPanel runId={runId} onOpenBac={onOpenBac} />
      </TabPanel>

      <TabPanel active={activeTab === 'pricing'}>
        <PricingCatalogPanel program={program} limit={TAB_LIMIT} />
      </TabPanel>

      <TabPanel active={activeTab === 'recurring'}>
        <RecurringRulesPanel program={program} />
      </TabPanel>
    </Box>
  );
};

export default BillingReconciliationWorkspace;
